<template>
  <v-container fluid>
    <v-card class="pa-6">
      <!-- Header -->
      <v-row align="center" class="mb-4">
        <v-col>
          <div class="text-h5 font-weight-bold">
            ✏️ Edit Docket | แก้ไข Docket
          </div>
          <div class="caption grey--text">
            {{ form.client_name
            }}<span v-if="form.inquiry_topic">
              &nbsp;|&nbsp; {{ form.inquiry_topic }}</span>
          </div>
        </v-col>
        <v-col class="d-flex justify-end">
          <v-btn outlined @click="$router.push(`/incorporate-docket-view/${docketId}`)">
            <v-icon left>mdi-arrow-left</v-icon>ยกเลิก
          </v-btn>
        </v-col>
      </v-row>

      <!-- ⭐ เพิ่มใหม่ — Tabs สลับระหว่าง Internal Work / Internal Timeline
           ตามแบบ incorporate-docket-add.vue โดยไม่แตะ logic เดิมของ Edit
           เนื้อหาเดิมทั้งหมดของ Edit ถูกย้ายไปอยู่ใน v-if="tabselect == 0" -->
      <div class="mb-5">
        <v-tabs v-model="tabselect" color="#55638E" class="custom-tabs" style="border-bottom: 1px solid #E0E0E0;">
          <v-tab>Internal Work</v-tab>
          <v-tab>Internal Timeline</v-tab>
        </v-tabs>
      </div>

      <!-- ⭐ เพิ่มใหม่ — แท็บ Internal Timeline
           รอ pageLoading เสร็จก่อน เพราะ TimeLineOperations ต้องใช้ form /
           timeline_case_id ที่โหลดข้อมูลจริงมาจาก loadDocket() แล้วเท่านั้น -->
      <v-card-text v-if="tabselect == 1 && !pageLoading">
        <TimeLineOperations :showaddItem="form" :statuslist="statuslist" :reportlist="reportlist"
          :modeOperations="modeOperations" :timelineStatus="timelineStatus" :status_note="status_note"
          :visible_check="visible_check" :module_name="module_name" :timeline_case_id="timeline_case_id"
          @timelineStatus="timelineStatus = $event" :showReportInv="false" @requestStatus="handleRequestStatus"
          @update:status_note="status_note = $event" @update:visible_check="visible_check = $event" />
      </v-card-text>

      <div v-if="pageLoading" class="text-center pa-10">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <!-- ⭐ แก้ไข — เดิมเป็น v-else ตรงๆ เปลี่ยนเป็น v-else-if="tabselect == 0"
           เพื่อให้โชว์เฉพาะตอนอยู่แท็บ Internal Work เนื้อหาข้างในทั้งหมด
           ไม่ถูกแตะ/แก้ไขเลยแม้แต่จุดเดียว -->
      <v-card v-else-if="tabselect == 0" outlined class="pa-6">
        <!-- Client Header (readonly, ดึงจาก Matter — ตอน Edit ไม่ให้เปลี่ยน Matter) -->
        <v-row class="mb-2">
          <v-col cols="12" md="4">
            <v-text-field :value="form.client_name" label="Client Name | ชื่อลูกค้า" outlined dense readonly
              background-color="grey lighten-4"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field :value="entityTypeLabel" label="Entity Type | ประเภทลูกค้า" outlined dense readonly
              background-color="grey lighten-4"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field :value="form.registration_no" label="Registration No. | เลขทะเบียน" outlined dense readonly
              background-color="grey lighten-4"></v-text-field>
          </v-col>
        </v-row>

        <!-- Section: Inquiry Information -->
        <div class="form-section-title mb-3 mt-2">📋 INQUIRY INFORMATION</div>
        <v-row>
          <v-col cols="12">
            <v-text-field v-model="form.inquiry_topic" label="Inquiry Topic | หัวข้อคำสอบถาม *" outlined dense
              placeholder="หัวข้อคำสอบถาม" :rules="[rules.required]"></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.date_received" label="Date Received | วันที่รับเรื่อง *" outlined dense
              type="date" :rules="[rules.required]"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.date_submitted" label="Date Submitted | วันที่ส่งคำขอ" outlined dense
              type="date"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <!-- ⭐ แก้ไข — บังคับกรอก Next Due Date เพราะหน้า Incorporate
                 Overview (IncorporateOverview.vue) ใช้ field นี้เป็นตัวหลัก
                 ในการคำนวณสถานะ Overdue / Due Soon และรายการ "Docket ที่
                 ใกล้ครบกำหนด" (upcomingDockets) ทั้งหมด — ถ้าเว้นว่างไว้
                 docket รายการนี้จะไม่ถูกนับรวมในหน้า Overview เลย -->
            <v-text-field v-model="form.next_due_date" label="Next Due Date | วันครบกำหนดถัดไป *" outlined dense
              type="date" :rules="[rules.required]"></v-text-field>
          </v-col>
        </v-row>

        <!-- Section: Document Category (single-select) -->
        <div class="form-section-title mb-3 mt-4 d-flex align-center flex-wrap" style="gap: 8px">
          <span>📁 DOCUMENT CATEGORY | ชนิดเอกสาร</span>
          <v-chip x-small label color="grey lighten-3">Single-select • ไม่บังคับ / Optional</v-chip>
        </div>
        <v-alert dense class="caption mb-3 business-rule-alert">
          <strong>Business Rule:</strong> เลือกได้เพียง 1 ประเภท (Single
          selection) — ไม่บังคับกรอก
        </v-alert>
        <v-row>
          <v-col cols="12" md="6" v-for="cat in docCategoryOptions" :key="cat">
            <div :class="[
              'doccat-item',
              form.doc_category === cat ? 'doccat-selected' : '',
            ]" @click="selectDocCategory(cat)">
              <div :class="[
                'tracker-radio',
                form.doc_category === cat ? 'tracker-radio-checked' : '',
              ]">
                <div v-if="form.doc_category === cat" class="tracker-radio-dot"></div>
              </div>
              <span class="doccat-label">{{ cat }}</span>
            </div>
          </v-col>
        </v-row>
        <!-- ⭐ เตือนถ้าค่าที่บันทึกไว้ใน DB ไม่ตรงกับ option ปัจจุบันสักตัวเลย
             (เช่น docCategoryOptions ถูกแก้ข้อความหลัง docket นี้ถูกสร้าง)
             เพื่อให้ผู้ใช้รู้ตัวว่าทำไมไม่เห็นช่องไหนถูกไฮไลต์ แทนที่จะงงเงียบ ๆ -->
        <v-alert v-if="
          form.doc_category && !docCategoryOptions.includes(form.doc_category)
        " dense type="warning" outlined class="caption mb-3">
          ค่าเดิมที่บันทึกไว้คือ "{{ form.doc_category }}"
          ซึ่งไม่ตรงกับตัวเลือกปัจจุบันสักอัน —
          เลือกใหม่อีกครั้งเพื่ออัปเดตให้ตรง
        </v-alert>

        <!-- Section: Existing Attachments (ไฟล์ที่อัปโหลดไว้แล้ว) -->
        <div class="form-section-title mb-3 mt-4">
          📎 EXISTING ATTACHMENTS | ไฟล์เอกสารเดิม
        </div>
        <v-card outlined class="mb-4">
          <v-card-text v-if="existingAttachments.length === 0" class="caption grey--text">
            ไม่มีไฟล์เอกสารที่แนบไว้
          </v-card-text>
          <v-list v-else dense>
            <v-list-item v-for="(file, i) in existingAttachments" :key="'existing-' + i" class="attachment-row">
              <v-list-item-icon>
                <v-icon color="grey darken-1">{{ fileIcon(file) }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="caption">{{
                  file
                }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="d-flex flex-row" style="gap: 6px;">
                <!-- พรีวิว: เปิดแท็บใหม่ชี้ไปที่ไฟล์บน Production Storage ตรง ๆ
                     ใช้ได้กับรูปภาพและ PDF ที่เบราว์เซอร์ preview ในตัวได้เลย -->
                <v-btn outlined x-small :href="attachmentUrl(file)" target="_blank">
                  <v-icon left x-small>mdi-eye-outline</v-icon>Preview
                </v-btn>
                <v-btn outlined x-small :href="attachmentUrl(file)" :download="file" target="_blank">
                  <v-icon left x-small>mdi-download</v-icon>Download
                </v-btn>
                <v-btn outlined x-small color="red" :loading="removingFile === file"
                  @click="confirmRemoveAttachment(file)">
                  <v-icon left x-small>mdi-delete-outline</v-icon>ลบ
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>

        <!-- Section: Upload New Document (เพิ่มไฟล์ใหม่เท่านั้น) -->
        <div class="form-section-title mb-3 mt-4">
          ➕ UPLOAD NEW DOCUMENT | เพิ่มไฟล์เอกสารใหม่
        </div>
        <v-card outlined class="pa-6 text-center upload-zone mb-4">
          <div style="font-size: 30px">📂</div>
          <div class="caption mt-2">
            คลิกเพื่อเลือกไฟล์ หรือลาก&amp;วางไฟล์ที่นี่
          </div>
          <div class="caption grey--text">
            PDF, DOCX, JPG, PNG — ขนาดไม่เกิน 25 MB — ไฟล์ที่เลือกที่นี่จะถูก
            <strong>เพิ่มต่อ</strong> จากไฟล์เดิมด้านบน ไม่ได้แทนที่
          </div>
          <v-file-input v-model="form.attachments" outlined dense multiple small-chips show-size
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" prepend-icon="mdi-paperclip" label="เลือกไฟล์เอกสารใหม่"
            class="mt-4 mx-auto" style="max-width: 480px"></v-file-input>
        </v-card>

        <!-- Section: Change Tracker -->
        <div class="form-section-title mb-3 mt-4">
          🔄 CHANGE TRACKER | การเปลี่ยนแปลงที่เกี่ยวข้อง
        </div>
        <v-alert dense class="caption mb-3 business-rule-alert">
          <strong>Business Rule:</strong> เลือกประเภทการเปลี่ยนแปลงได้เพียง 1
          ประเภท (Single selection) — ข้อมูลจะดึงมาจากหน้า Matter
          ของลูกความและอัปเดตใน Docket อัตโนมัติ<br />
          เมื่อเลือกแล้ว ระบบ<strong>บังคับให้ระบุหน่วยงานที่ต้องไปดำเนินการเปลี่ยนแปลง</strong>
          (เช่น กรมพัฒนาธุรกิจการค้า, สรรพากร, ประกันสังคม ฯลฯ) ได้มากกว่า 1
          หน่วยงานต่อการเปลี่ยนแปลง
        </v-alert>

        <v-row>
          <v-col cols="12" md="4" v-for="tr in trackerOptions" :key="tr.key">
            <div :class="[
              'tracker-item',
              form.change_tracker.includes(tr.key) ? 'tracker-selected' : '',
            ]" @click="selectTracker(tr.key)">
              <div :class="[
                'tracker-radio',
                form.change_tracker.includes(tr.key)
                  ? 'tracker-radio-checked'
                  : '',
              ]">
                <div v-if="form.change_tracker.includes(tr.key)" class="tracker-radio-dot"></div>
              </div>
              <span class="doccat-label">{{ tr.label }}</span>
            </div>
          </v-col>
        </v-row>

        <!-- Detail panel ของ Change Tracker ที่เลือก -->
        <div v-for="tr in trackerOptions" :key="'detail-' + tr.key">
          <v-card v-if="form.change_tracker.includes(tr.key)" outlined class="pa-4 mt-2 mb-2" color="blue lighten-5">
            <!-- Shareholders -->
            <div v-if="tr.key === 'shareholders'">
              <table class="repeatable-table" style="width: 100%">
                <thead>
                  <tr>
                    <th>ชื่อ–นามสกุล</th>
                    <th>สัญชาติ</th>
                    <th>หุ้นสามัญ</th>
                    <th>หุ้นบุริมสิทธิ</th>
                    <th style="width: 40px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(sh, i) in trackerDetail.shareholders" :key="'sh' + i">
                    <td>
                      <v-text-field v-model="sh.full_name" dense hide-details solo flat
                        placeholder="ชื่อ–นามสกุล"></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-model="sh.nationality" dense hide-details solo flat
                        placeholder="สัญชาติ"></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-model="sh.ordinary_shares" dense hide-details solo flat type="number"
                        placeholder="0"></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-model="sh.preferred_shares" dense hide-details solo flat type="number"
                        placeholder="0"></v-text-field>
                    </td>
                    <td>
                      <v-btn icon x-small color="red" @click="trackerDetail.shareholders.splice(i, 1)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
              <v-btn text small color="primary" @click="addShareholderRow">
                <v-icon left small>mdi-plus</v-icon>เพิ่มผู้ถือหุ้น
              </v-btn>
            </div>

            <!-- Directors -->
            <div v-else-if="tr.key === 'directors'">
              <table class="repeatable-table" style="width: 100%">
                <thead>
                  <tr>
                    <th>ชื่อ–นามสกุล</th>
                    <th>สัญชาติ</th>
                    <th>มีอำนาจลงนาม</th>
                    <th style="width: 40px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(dir, i) in trackerDetail.directors" :key="'dir' + i">
                    <td>
                      <v-text-field v-model="dir.full_name" dense hide-details solo flat
                        placeholder="ชื่อ–นามสกุล"></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-model="dir.nationality" dense hide-details solo flat
                        placeholder="สัญชาติ"></v-text-field>
                    </td>
                    <td>
                      <v-select v-model="dir.authorized" :items="['Yes', 'No']" dense hide-details solo flat></v-select>
                    </td>
                    <td>
                      <v-btn icon x-small color="red" @click="trackerDetail.directors.splice(i, 1)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
              <v-btn text small color="primary" @click="addDirectorRow">
                <v-icon left small>mdi-plus</v-icon>เพิ่มกรรมการ
              </v-btn>
            </div>

            <!-- Registered Capital -->
            <v-row v-else-if="tr.key === 'reg-capital'" style="max-width: 320px">
              <v-col cols="12">
                <v-text-field v-model="trackerDetail.reg_capital" label="Registered Capital (THB) — ใหม่" outlined dense
                  type="number" placeholder="0.00"></v-text-field>
              </v-col>
            </v-row>

            <!-- Paid-up Capital -->
            <v-row v-else-if="tr.key === 'paidup-capital'" style="max-width: 320px">
              <v-col cols="12">
                <v-text-field v-model="trackerDetail.paidup_capital" label="Paid-up Capital (THB) — ใหม่" outlined dense
                  type="number" placeholder="0.00"></v-text-field>
              </v-col>
            </v-row>

            <!-- Company Name -->
            <v-row v-else-if="tr.key === 'company-name'" style="max-width: 420px">
              <v-col cols="12">
                <v-text-field v-model="trackerDetail.company_name" label="Company Name — ใหม่" outlined dense
                  placeholder="ชื่อบริษัทใหม่"></v-text-field>
              </v-col>
            </v-row>

            <!-- Registered Address -->
            <v-row v-else-if="tr.key === 'reg-address'">
              <v-col cols="12">
                <v-text-field v-model="trackerDetail.reg_address" label="Registered Address — ใหม่" outlined dense
                  placeholder="ที่อยู่ใหม่"></v-text-field>
              </v-col>
            </v-row>

            <!-- Auditor -->
            <v-row v-else-if="tr.key === 'auditor'">
              <v-col cols="12" md="4">
                <v-text-field v-model="trackerDetail.audit_firm" label="Audit Firm" outlined dense
                  placeholder="ชื่อบริษัท"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="trackerDetail.auditor_name" label="Auditor Name" outlined dense
                  placeholder="ชื่อผู้สอบบัญชี"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="trackerDetail.auditor_license_no" label="License No." outlined dense
                  placeholder="เลขที่ใบอนุญาต"></v-text-field>
              </v-col>
            </v-row>

            <!-- Accountant -->
            <v-row v-else-if="tr.key === 'accountant'">
              <v-col cols="12" md="6">
                <v-text-field v-model="trackerDetail.accountant_firm" label="Accountant Firm" outlined dense
                  placeholder="ชื่อสำนักงานบัญชี"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="trackerDetail.accountant_name" label="Accountant Name" outlined dense
                  placeholder="ชื่อผู้ทำบัญชี"></v-text-field>
              </v-col>
            </v-row>

            <!-- Others -->
            <v-row v-else-if="tr.key === 'others'">
              <v-col cols="12">
                <v-textarea v-model="trackerDetail.others_detail" label="Others — รายละเอียด" outlined dense rows="2"
                  placeholder="ระบุรายละเอียดการเปลี่ยนแปลงอื่น ๆ"></v-textarea>
              </v-col>
            </v-row>

            <!-- Agency selection — บังคับเลือกอย่างน้อย 1 หน่วยงาน -->
            <div class="agency-block">
              <div class="agency-title">
                <span>🏛️ หน่วยงานที่ต้องดำเนินการ
                  <span class="red--text">*</span></span>
                <v-chip x-small label color="amber lighten-2">เลือกได้มากกว่า 1 หน่วยงาน</v-chip>
              </div>
              <v-row>
                <v-col cols="12" md="4" v-for="agency in agencyOptions" :key="agency">
                  <div :class="[
                    'agency-item',
                    isAgencySelected(tr.key, agency)
                      ? 'agency-item-selected'
                      : '',
                  ]" @click="toggleAgency(tr.key, agency)">
                    <v-checkbox :input-value="isAgencySelected(tr.key, agency)" hide-details dense
                      class="ma-0 pa-0 mr-2" @click.native.stop="toggleAgency(tr.key, agency)"></v-checkbox>
                    <span>{{ agency }}</span>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-card>
        </div>

        <!-- Section: Remark -->
        <div class="form-section-title mb-3 mt-4">💬 REMARK</div>
        <v-textarea v-model="form.remark" outlined rows="3" placeholder="หมายเหตุ / Remark"></v-textarea>

        <!-- Section: Status -->
        <div class="form-section-title mb-3 mt-2">
          📌 สถานะเอกสาร | STATUS
          <span class="red--text">*</span>
        </div>
        <v-row>
          <v-col cols="12" md="4">
            <!-- ⭐ แก้ไข — เดิม items ผูกกับ statusOptions (hardcode string
                 label/value) เปลี่ยนมาผูกกับ statuslist (config จริงจาก
                 DB ตาราง client_timeline_status_config) และเพิ่ม @change
                 เรียก sendToStatus เพื่อ sync ค่ากับแท็บ Internal Timeline
                 เหมือนกับที่ incorporate-docket-add.vue ทำ — statusOptions
                 เดิมยังเก็บไว้ในไฟล์นี้ (ไม่ลบ) เผื่อมีจุดอื่นอ้างอิงอยู่
                 แค่ v-select ตัวนี้ไม่ได้ใช้กับมันแล้ว -->
            <v-select v-model="form.status" :items="statuslist" item-text="descriptions" item-value="module_id" outlined
              dense @change="sendToStatus"></v-select>
            <v-chip small label :color="getDocketStatusColor(form.status)"
              :text-color="getDocketStatusTextColor(form.status)">
              <v-icon left x-small>mdi-circle</v-icon>{{ form.status }}
            </v-chip>
          </v-col>
        </v-row>
        <!-- ============ SECTION E: การเงิน / INVOICE ============ -->
        <v-divider class="mt-10"></v-divider>
        <div class="sec mt-6">
          <div class="sec-label">
            E · การเงิน &amp; สถานะ Invoice
          </div>

          <div class="invoice-box">
            <v-row>
              <v-col cols="12" md="4">
                <div class="field-label">เลขที่ใบเสนอราคา / Quotation ID</div>
                <v-autocomplete v-model="formQutations.salesOrderId" :items="salesOrderList" item-value="quotation_id"
                  item-text="doc_no" placeholder="SO-69-0001" outlined dense hide-details="auto"
                  background-color="white" clearable @change="onSelectSO" />
              </v-col>
              <v-col cols="12" md="4">
                <div class="field-label">Price</div>
                <v-text-field v-model="formQutations.price" type="number" outlined dense hide-details="auto"
                  placeholder="฿" background-color="#f2f2f2" disabled />
              </v-col>

            </v-row>
            <v-row v-if="modeOperations == 'New' || (modeOperations == 'Edit' && invoiceList.length === 0)">
              <v-col cols="12" md="3">
                <div class="field-label">
                  เลขที่ Invoice
                  <v-tooltip top max-width="260">
                    <template #activator="{ on, attrs }">
                      <span class="info-tag" v-bind="attrs" v-on="on">i</span>
                    </template>
                    <span>แนะนำเพิ่ม: บันทึกเลขบิลเมื่อออกแล้ว เพื่อตรวจสอบย้อนหลัง</span>
                  </v-tooltip>

                </div>
                <v-text-field placeholder="INV-… (auto เมื่อออก)" disabled outlined dense hide-details="auto"
                  background-color="#f2f2f2" />
              </v-col>
              <v-col cols="12" md="3">
                <div class="field-label">
                  วันที่ออก Invoice
                </div>
                <!-- <v-text-field v-model="form.invoiceDate" type="date" outlined dense hide-details="auto"
                                    background-color="white" /> -->
                <v-menu v-model="invoiceMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                  min-width="auto" disabled>
                  <template #activator="{ on, attrs }">
                    <v-text-field placeholder="dd/mm/yyyy" readonly outlined dense hide-details="auto" v-bind="attrs"
                      v-on="on" append-icon="mdi-calendar" background-color="#f2f2f2" />
                  </template>
                  <v-date-picker v-model="formQutations.invoiceDate" no-title locale="th"
                    @input="invoiceMenu = false" />
                </v-menu>
              </v-col>
              <v-col cols="12" md="3">
                <div class="field-label">
                  วันครบกำหนดชำระ
                </div>
                <!-- <v-text-field v-model="form.dueDate" type="date" outlined dense hide-details="auto"
                                    background-color="white" /> -->
                <v-menu v-model="invoiceMenuPay" :close-on-content-click="false" transition="scale-transition" offset-y
                  min-width="auto" disabled>
                  <template #activator="{ on, attrs }">
                    <v-text-field placeholder="dd/mm/yyyy" readonly outlined dense hide-details="auto" v-bind="attrs"
                      v-on="on" append-icon="mdi-calendar" background-color="#f2f2f2" />
                  </template>
                  <v-date-picker v-model="formQutations.dueDate" no-title locale="th" @input="invoiceMenuPay = false" />
                </v-menu>
              </v-col>
              <v-col cols="12" md="3">
                <div class="field-label">
                  Invoice Status
                  <v-tooltip top max-width="260">
                    <template #activator="{ on, attrs }">
                      <span class="info-tag" v-bind="attrs" v-on="on">i</span>
                    </template>
                    <span>หัวใจของโมดูล: ติดตามรอบบิลแบบเดียวกับ Immigration</span>
                  </v-tooltip>

                </div>
                <v-select placeholder="auto เมื่อออก" :items="invoiceStatusList" outlined dense hide-details="auto"
                  background-color="#f2f2f2" disabled />
              </v-col>
            </v-row>
            <template v-if="modeOperations == 'Edit' && invoiceList.length > 0">
              <v-row v-for="(item, index) in invoiceList" :key="item.id">
                <v-col cols="12" md="3">
                  <div class="field-label">
                    เลขที่ Invoice
                    <v-tooltip top max-width="260">
                      <template #activator="{ on, attrs }">
                        <span class="info-tag" v-bind="attrs" v-on="on">i</span>
                      </template>
                      <span>แนะนำเพิ่ม: บันทึกเลขบิลเมื่อออกแล้ว เพื่อตรวจสอบย้อนหลัง</span>
                    </v-tooltip>
                  </div>
                  <v-text-field :value="item.invoice_display" placeholder="INV-… (auto เมื่อออก)" disabled outlined
                    dense hide-details="auto" background-color="#f2f2f2" />
                </v-col>
                <v-col cols="12" md="3">
                  <div class="field-label">วันที่ออก Invoice</div>
                  <v-text-field :value="item.created_at" disabled placeholder="dd/mm/yyyy" readonly outlined dense
                    hide-details="auto" append-icon="mdi-calendar" background-color="#f2f2f2" />
                </v-col>
                <v-col cols="12" md="3">
                  <div class="field-label">วันครบกำหนดชำระ</div>
                  <v-text-field :value="item.inv_date" disabled placeholder="dd/mm/yyyy" readonly outlined dense
                    hide-details="auto" append-icon="mdi-calendar" background-color="#f2f2f2" />
                </v-col>
                <v-col cols="12" md="2">
                  <div class="field-label">
                    Invoice Status
                    <!-- <v-tooltip top max-width="260">
                      <template #activator="{ on, attrs }">
                        <span class="info-tag" v-bind="attrs" v-on="on">i</span>
                      </template>
                      <span>หัวใจของโมดูล: ติดตามรอบบิลแบบเดียวกับ Immigration</span>
                    </v-tooltip> -->
                  </div>
                  <v-text-field :value="item.invoice_status" placeholder="auto เมื่อออก" outlined dense
                    hide-details="auto" background-color="#f2f2f2" disabled />
                </v-col>
                <v-col cols="12" md="1" class="d-flex align-center justify-center" style="margin-top: 24px;">
                  <v-tooltip top>
                    <template #activator="{ on, attrs }">
                      <v-btn icon color="primary" v-bind="attrs" v-on="on" @click="previewInvoice(item)">
                        <v-icon>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>ดูรายละเอียด</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </template>
          </div>
        </div>

      </v-card>
      <!-- Actions -->
      <v-row class="mt-4">
        <v-col class="d-flex justify-end" style="gap: 10px">
          <v-btn color="#0A7EA5" :loading="loadingSave" @click="updateDocket('Payment')"
            :disabled="!formQutations.salesOrderId" style="color: #ffffff;">
            <v-icon left>mdi-content-save</v-icon>Request Payment
          </v-btn>
          <v-btn color="#0A7EA5" dark :loading="loadingSave" @click="updateDocket('Save')">
            <v-icon left>mdi-content-save</v-icon>Update
          </v-btn>
          <v-btn outlined @click="$router.push(`/incorporate-docket-view/${docketId}`)">Cancel</v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Confirm Delete Attachment Dialog -->
    <v-dialog v-model="dialogConfirmDelete" max-width="420">
      <v-card>
        <v-card-title class="text-h6">ยืนยันการลบไฟล์</v-card-title>
        <v-card-text>
          ต้องการลบไฟล์ <strong>{{ fileToDelete }}</strong> หรือไม่?
          การลบนี้จะลบไฟล์ออกจากทั้ง Production และ Backup Storage ทันที
          และไม่สามารถย้อนกลับได้
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogConfirmDelete = false">ยกเลิก</v-btn>
          <v-btn color="red" dark @click="removeExistingAttachment(fileToDelete)">ลบไฟล์</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ⭐ เพิ่มใหม่ — Timeline Update Required dialog ตามแบบ
         incorporate-docket-add.vue ใช้เตือนตอนกด Update แต่ยังไม่เคยกรอก
         Internal Timeline (timelineStatus == 0 หรือ status_note ว่าง) -->
    <v-dialog v-model="dialogDelete" max-width="600px">
      <v-card class="pa-10" style="border: 5px solid #e5e7eb;">
        <v-card-title style="padding-bottom: 20px;">
          <v-row justify="center">
            <span class="text-h4 font-weight-bold">Timeline Update Required</span>
          </v-row>
        </v-card-title>
        <br />
        <v-card-subtitle>
          <v-row justify="center" class="text-center">
            <span class="black--text text-body-1">
              Before saving this case, please update the internal timeline so the team can see the latest
              work
              status.
            </span>
          </v-row>
        </v-card-subtitle>
        <v-card-actions style="padding-top: 20px">
          <v-spacer></v-spacer>
          <v-btn depressed width="170" class="mr-3 " color="#0A7EA5" @click="deleteItemConfirm"
            style="border-radius: 20px; color: white; text-transform: none;">
            Update Timeline
          </v-btn>
          <v-btn depressed width="170" @click="closeDelete" color="red"
            style="border-radius: 20px; color: white; text-transform: none;">
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <SuccessDialog :status="dialogAdd" :text_color="text_color" :title="title" :message="message" />
    <SuccessDialogPush :status="dialogAddPush" :text_color="text_color" :title="title" :message="message"
      :link="link" />
  </v-container>
</template>

<script>
import api from "@/services/api"; // ⭐ เพิ่ม — ยังไม่มีใน edit เดิม ใช้เรียก timeline endpoints
import SuccessDialog from "@/components/cards/SuccessDialog";
import SuccessDialogPush from "@/components/cards/SuccessDialogPush";
import { server } from "@/services/constants";
import {
  getIncorporateDocketById,
  updateIncorporateDocket,
  // ⭐ เพิ่มใหม่ — บันทึก Change Tracker ทั้งชุด
  replaceIncorporateDocketChangeAll,
  // ⭐ เพิ่มใหม่ — ลบไฟล์แนบ 1 ไฟล์ออกจาก docket (ดู api_incorporate.js
  // ต้องเพิ่ม wrapper นี้ก่อน — เรียก DELETE /incorporate-docket/:id/attachment)
  removeIncorporateDocketAttachment,
} from "@/services/api-dtime/api_incorporate";
import TimeLineOperations from "../timeline/TimeLineOperations.vue"; // ⭐ เพิ่ม
import PDFCaseProgress from "@/utils/pdfClientTimeLine"; // ⭐ เพิ่ม
const moment = require("moment-timezone"); // ⭐ เพิ่ม

// ✅ ไฟล์ incorporate ถูกเก็บที่ Production Storage (เหมือน Matter)
// path จริงคือ {PRODUCTION_STORAGE_URL}/incorporatedocket/{docketId}/{filename}
// ตาม static route ที่ตั้งไว้ใน production storage app.js:
//   app.use("/incorporatedocket", express.static(...))
const INCORPORATE_DOCKET_FILE_URL = process.env.VUE_APP_PRODUCTION_STORAGE_URL
  ? `${process.env.VUE_APP_PRODUCTION_STORAGE_URL}/incorporatedocket`
  : "http://localhost:3001/incorporatedocket";

export default {
  name: "incorporate-docket-edit",
  components: { SuccessDialog, SuccessDialogPush, TimeLineOperations }, // ⭐ เพิ่ม TimeLineOperations
  data() {
    return {
      docketId: null,
      pageLoading: true,
      matterId: null,
      loadingSave: false,

      fileIncorporateUrl: INCORPORATE_DOCKET_FILE_URL,

      // ⭐ ไฟล์ที่มีอยู่แล้วบน server (จาก docket.file_attachments) — แสดงเป็น
      // รายการ read-only พร้อมปุ่ม preview/download/ลบ แยกออกจาก form.attachments
      // ซึ่งเป็น File object ใหม่ที่ยังไม่อัปโหลด (v-file-input สร้าง File object
      // จาก URL ที่มีอยู่แล้วไม่ได้ — เป็นข้อจำกัดของ browser ไม่ใช่บั๊ก จึงต้อง
      // แยก 2 state นี้ออกจากกันเสมอ)
      existingAttachments: [],
      removingFile: null,
      dialogConfirmDelete: false,
      fileToDelete: null,

      rules: {
        required: (v) =>
          (v !== null && v !== undefined && String(v).trim() !== "") ||
          "กรุณากรอกข้อมูล",
      },

      form: {
        matter_id: null,
        client_name: "",
        entity_type: "",
        registration_no: "",
        inquiry_topic: "",
        date_received: "",
        date_submitted: "",
        next_due_date: "",
        doc_category: "",
        attachments: [], // ไฟล์ใหม่ที่ยังไม่อัปโหลด (File objects)
        change_tracker: [],
        remark: "",
        status: "In Progress",
      },

      // ✅ UI state เท่านั้น — ยังไม่มี table รองรับฝั่ง backend
      trackerDetail: {
        shareholders: [
          {
            full_name: "",
            nationality: "",
            ordinary_shares: 0,
            preferred_shares: 0,
          },
        ],
        directors: [{ full_name: "", nationality: "", authorized: "No" }],
        reg_capital: "",
        paidup_capital: "",
        company_name: "",
        reg_address: "",
        audit_firm: "",
        auditor_name: "",
        auditor_license_no: "",
        accountant_firm: "",
        accountant_name: "",
        others_detail: "",
      },
      // ✅ UI state: { [trackerKey]: ['agency1', 'agency2'] }
      selectedAgencies: {},

      docCategoryOptions: [
        "Company Incorporation Documents | เอกสารตั้งบริษัท",
        "Certified Copy + Registration Items + Application",
        "Memorandum of Association | บช.บริคณห์สนธิ",
        "Articles of Association | ข้อบังคับ",
        "List of Shareholders (Form BorJor.5) | บัญชีรายชื่อผู้ถือหุ้น",
        "Financial Statement Submission Forms (SorBorChor.3)",
        "Tax Information | ข้อมูลภาษี",
        "Share Certificates / Share Register | ใบหุ้น/ทะเบียนหุ้น",
        "Minutes of Board of Directors' Meeting",
        "Minutes of Extraordinary Shareholders' Meeting",
        "Minutes of Annual General Shareholders' Meeting",
        "BOI Documents / Promotion Certificate",
        "Various Licenses / Permits | ใบอนุญาตต่าง ๆ",
        "Agreements / Contracts | สัญญาต่าง ๆ",
        "Legal Opinion | ความเห็นทางกฎหมาย",
        "Work Rules | ข้อบังคับการทำงาน",
        "Due Diligence Report",
        "Others | อื่น ๆ",
      ],

      trackerOptions: [
        { key: "shareholders", label: "👥 Shareholders | ผู้ถือหุ้น" },
        { key: "directors", label: "👔 Directors | กรรมการบริษัท" },
        { key: "reg-capital", label: "💰 Registered Capital (THB)" },
        { key: "paidup-capital", label: "💵 Paid-up Capital (THB)" },
        { key: "company-name", label: "🏢 Company Name | ชื่อบริษัท" },
        { key: "reg-address", label: "📍 Registered Address (Head Office)" },
        { key: "auditor", label: "📋 Auditor | ผู้สอบบัญชี" },
        { key: "accountant", label: "📊 Accountant | ผู้ทำบัญชี" },
        { key: "others", label: "📝 Others | อื่น ๆ" },
      ],

      agencyOptions: [
        "กรมพัฒนาธุรกิจการค้า (DBD)",
        "คปภ. (OIC)",
        "ประกันสังคม (SSO)",
        "สรรพากร (Revenue)",
        "การนิคมอุตสาหกรรม (IEAT)",
        "สถานฑูต (Embassy)",
        "กรมการกงสุล (Consular)",
        "สำนักงานเขต / เทศบาล / อบต.",
        "BOI",
        "กรมศุลกากร (Customs)",
        "อื่น ๆ (Others)",
      ],

      // ⚠️ เดิมของ Edit — ไม่ได้ลบ เผื่อจุดอื่นในระบบยังอ้างอิงอยู่
      // แต่ v-select ของ Status ไม่ได้ผูกกับตัวนี้แล้ว (เปลี่ยนไปใช้ statuslist
      // ที่โหลดจาก client_timeline_status_config แทน เพื่อให้ map ไป
      // timeline_status_id ได้ตามที่ระบบ Timeline ต้องการ)
      statusOptions: [
        { label: "In Progress | กำลังดำเนินการ", value: "In Progress" },
        { label: "Complete | เสร็จสิ้น", value: "Complete" },
        {
          label: "Request Payment | ขอเรียกเก็บเงิน",
          value: "Request Payment",
        },
        { label: "Cancelled | ยกเลิก", value: "Cancelled" },
      ],

      dialogAdd: false,
      dialogAddPush: false,
      text_color: "text-h5 green--text text-center",
      title: "",
      message: "",
      link: "",

      // ⭐ เพิ่มใหม่ทั้งหมด — สำหรับ Internal Timeline tab (ตามแบบ
      // incorporate-docket-add.vue) modeOperations ส่งเป็น "Edit" เพื่อให้
      // TimeLineOperations.vue โหลดประวัติ timeline เดิมที่เคยมีมาแสดงด้วย
      tabselect: 0,
      timelineStatus: 0,
      status_note: "",
      visible_check: false,
      dialogDelete: false,
      timeline_case_id: 0,
      module_name: "Corporate",
      modeOperations: "Edit",
      reportlist: [],
      statuslist: [],
      //quotations
      formQutations: {
        salesOrderId: 0,
        price: 0
      },
      salesOrderList: [],
      invoiceList: [],
      invoiceMenu: false,
      invoiceMenuPay: false,
      invoiceStatusList: ["Draft", "Requested", "Invoiced", "Paid"],
      quotationscheckstatus: '',
      client_id: 0,
      client_name_show: "",
      matterdatalist: [],
    };
  },
  computed: {
    entityTypeLabel() {
      const map = {
        "Limited Company": "Limited Company | บริษัทจำกัด",
        "Public Company Limited": "Public Company Limited | บริษัทมหาชนจำกัด",
        Foundation: "Foundation | มูลนิธิ",
        "Representative Office": "Representative Office | สำนักงานผู้แทนการค้า",
        Individual: "Individual | บุคคลธรรมดา",
      };
      return map[this.form.entity_type] || this.form.entity_type || "-";
    },
  },
  async beforeCreate() {
    this.$store.state.navMenu = true;
  },
  async mounted() {
    await this.loadDataInvoice(this.$route.params.id);
    await this.loaddataSalesOrder();
    await this.loadMatterList();
    this.$hideLoader();
    this.docketId = this.$route.params.id;

    // ⭐ เพิ่ม — TimeLineOperations ใช้ timeline_case_id ตัวนี้ query ประวัติ
    // (ต้องผูกกับ id เดียวกับ docketId ของ incorporate docket เสมอ)
    this.timeline_case_id = Number(this.docketId);

    // ⭐ เพิ่ม — โหลด statuslist (client_timeline_status_config) ก่อน
    // loadDocket() เพื่อให้ sendToStatus() หา module_id ของ docket.status
    // ที่โหลดมาเจอได้ทันทีตอนซิงค์ค่าด้านล่าง
    await this.loaddataLitigationCourtStatus();

    await this.loadDocket(this.docketId); // เดิม ไม่แตะ

    // ⭐ เพิ่ม — ซิงค์ timelineStatus ให้ตรงกับ form.status ปัจจุบันของ
    // docket ตั้งแต่โหลดหน้าครั้งแรก (ไม่ต้องรอผู้ใช้คลิกเปลี่ยน status เอง)
    this.sendToStatus(this.form.status);

    this.pageLoading = false; // เดิม ไม่แตะ
  },
  methods: {
    async loadMatterList() {
      try {
        const res = await api.getAllIncorporateMatter();
        const list = Array.isArray(res.data) ? res.data : [];
        this.matterdatalist = list;

      } catch (error) {
        console.error("Error loading matter list:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "ไม่สามารถโหลดรายชื่อลูกค้าจาก Matter ได้",
          "text-h5 red--text text-center",
        );
      }

    },
    //quotations 
    async loaddataSalesOrder() {
      try {
        const res = await api.getQuotatinsForDocketCore(Number(localStorage.getItem(server.COMPANYID)));
        this.salesOrderList = res.data || [];

      } catch (error) {
        console.error("loaddataSalesOrder error:", error);
      }
    },
    onSelectSO(value) {
      if (!value) {
        this.formQutations.price = "";
        return
      }
      // หา object ของ SO ที่เลือก
      const so = this.salesOrderList.find(item => item.quotation_id === value);
      if (so) {
        this.quotationscheckstatus = so.status;
        this.formQutations.price = Number(so.subtotal);
      }
    },
    async loadDataInvoice(id) {
      try {
        var dataserach = {
          module_name: 'Incorporation',
          module_id: id,
          company_id: Number(localStorage.getItem(server.COMPANYID)),
        }
        const res = await api.getQuotatinsinvoiceListCore({
          params: dataserach
        })
        this.invoiceList = res.data.data || [];
      } catch (error) {
        console.error("loadDataInvoice error:", error);
      }
    },
    //end quotattions
    previewInvoice(item) {
      const routeData = this.$router.resolve(
        `/edit-invoice/${item.inv_id_preview}/inv_h/edit`
      );
      window.open(routeData.href, '_blank');
    },
    // ⭐ เพิ่มใหม่ทั้งบล็อกนี้ — สำหรับ Internal Timeline (ก็อปแบบมาจาก
    // incorporate-docket-add.vue)
    closeDelete() {
      this.dialogDelete = false;
    },
    deleteItemConfirm() {
      this.tabselect = 1;
      this.dialogDelete = false;
    },
    handleRequestStatus(value) {
      this.form.status = value;
    },
    sendToStatus(value) {
      const selectedItem = this.statuslist.find(
        (item) => item.module_id === value
      );
      // ⭐ กัน error กรณี form.status เดิม (เช่นค่า hardcode เก่าจาก DB
      // ที่ไม่ตรงกับ module_id ไหนใน statuslist ที่โหลดมาจริง)
      if (selectedItem) {
        this.timelineStatus = Number(selectedItem.timeline_status_id);
      }
    },
    async loaddataLitigationCourtStatus() {
      const result = await api.getClientTimelineStatusCongFigByModule(
        "Corporate"
      );
      this.statuslist = (result.data || []).map((item) => ({
        ...item,
        module_id: item.module_id,
      }));
    },
    // ⭐ จบบล็อกเพิ่มใหม่สำหรับ Timeline

    async loadDocket(id) {
      try {
        const res = await getIncorporateDocketById(id);
        const docket = res.data;
        if (!docket) return;
        console.log(docket)
        this.client_id = docket.matter.id;
        this.client_name_show = docket.matter.client_name;
        this.matterId = docket.matter_id || null;
        this.form.matter_id = docket.matter_id || null;
        this.form.client_name = docket.client_name || "";
        this.form.entity_type =
          docket.matter?.entity_type || docket.matter_entity_type || "";
        this.form.registration_no =
          docket.matter?.registration_no || docket.matter_registration_no || "";
        this.form.inquiry_topic = docket.inquiry_topic || "";
        this.form.date_received = this.toDateInput(docket.date_received);
        this.form.date_submitted = this.toDateInput(docket.date_submitted);
        this.form.next_due_date = this.toDateInput(docket.next_due_date);

        // ⭐ Document Category — single-select เก็บเป็น string เดี่ยวอยู่แล้ว
        // ใน DB แต่เผื่อ docket เก่าที่ยังมี comma ติดมา (สมัยที่ยังเป็น
        // multi-select ตามที่เห็นใน wireframe เดิม) ให้ split แล้วเอาตัวแรก
        this.form.doc_category = docket.doc_category
          ? docket.doc_category
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)[0] || ""
          : "";

        // ⭐ FIX (บั๊กหลักที่พบ) — เดิมไฟล์นี้ไม่มีบรรทัดนี้เลย ทำให้
        // ไฟล์แนบที่เคยอัปโหลดไว้ไม่ขึ้นในหน้า Edit เลย
        // file_attachments เก็บเป็น comma-separated filename string
        this.existingAttachments = docket.file_attachments
          ? docket.file_attachments
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
          : [];

        this.form.remark = docket.remark || "";
        this.form.status = docket.status || "In Progress";
        this.formQutations.salesOrderId = docket.quotation_id
        await this.onSelectSO(this.formQutations.salesOrderId)
        // ⭐ โหลด Change Tracker เดิมกลับเข้าฟอร์ม จากตาราง incorporate_docket_change
        // (docket.changes มาจาก findById ที่ include changes + agencies ไว้แล้วฝั่ง backend)
        // ⚠️ ตอนนี้ Change Tracker เป็น single-select — ถ้า docket เก่ามีหลาย
        // changes ติดมา (จากตอนที่ยังเลือกได้หลายอัน) จะใช้แค่รายการแรกเท่านั้น
        if (Array.isArray(docket.changes) && docket.changes.length > 0) {
          this.form.change_tracker = [docket.changes[0].change_type];

          const c = docket.changes[0];
          const detail = c.change_detail || {};

          switch (c.change_type) {
            case "shareholders":
              this.trackerDetail.shareholders =
                Array.isArray(detail.items) && detail.items.length > 0
                  ? detail.items.map((it) => ({
                    full_name: it.full_name || "",
                    nationality: it.nationality || "",
                    ordinary_shares: it.ordinary_shares || 0,
                    preferred_shares: it.preferred_shares || 0,
                  }))
                  : this.trackerDetail.shareholders;
              break;
            case "directors":
              this.trackerDetail.directors =
                Array.isArray(detail.items) && detail.items.length > 0
                  ? detail.items.map((it) => ({
                    full_name: it.full_name || "",
                    nationality: it.nationality || "",
                    authorized: it.authorized || "No",
                  }))
                  : this.trackerDetail.directors;
              break;
            case "reg-capital":
              this.trackerDetail.reg_capital = detail.amount ?? "";
              break;
            case "paidup-capital":
              this.trackerDetail.paidup_capital = detail.amount ?? "";
              break;
            case "company-name":
              this.trackerDetail.company_name = detail.name || "";
              break;
            case "reg-address":
              this.trackerDetail.reg_address = detail.address || "";
              break;
            case "auditor":
              this.trackerDetail.audit_firm = detail.audit_firm || "";
              this.trackerDetail.auditor_name = detail.auditor_name || "";
              this.trackerDetail.auditor_license_no =
                detail.auditor_license_no || "";
              break;
            case "accountant":
              this.trackerDetail.accountant_firm = detail.accountant_firm || "";
              this.trackerDetail.accountant_name = detail.accountant_name || "";
              break;
            case "others":
              this.trackerDetail.others_detail = detail.detail || "";
              break;
          }

          // ⭐ โหลดหน่วยงานที่เลือกไว้เดิมของ change ประเภทนี้
          this.$set(
            this.selectedAgencies,
            c.change_type,
            Array.isArray(c.agencies)
              ? c.agencies.map((a) => a.agency_name)
              : [],
          );
        }
        // else: docket เก่าที่ยังไม่มีข้อมูลในตาราง incorporate_docket_change
        // จะมีแค่ text เดิม (docket.change_tracker) ไม่มีรายละเอียด/หน่วยงาน
        // ให้กรอกต่อ — ต้องเลือกใหม่ถ้าต้องการแก้ไข (ตามที่ comment เดิมระบุไว้)
      } catch (error) {
        console.error("Error loading docket:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "ไม่สามารถโหลดข้อมูล Docket ได้",
          "text-h5 red--text text-center",
        );
      }
    },

    toDateInput(dateStr) {
      if (!dateStr) return "";
      try {
        return new Date(dateStr).toISOString().slice(0, 10);
      } catch {
        return "";
      }
    },

    // ⭐ สร้าง URL ไปยังไฟล์จริงบน Production Storage
    // path จริง: {PRODUCTION_STORAGE_URL}/incorporatedocket/{docketId}/{filename}
    attachmentUrl(filename) {
      return `${this.fileIncorporateUrl}/${this.docketId}/${filename}`;
    },

    // ⭐ เลือกไอคอนตามนามสกุลไฟล์ ให้พอแยกประเภทได้ในรายการ
    fileIcon(filename) {
      const ext = (filename.split(".").pop() || "").toLowerCase();
      if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext))
        return "mdi-file-image-outline";
      if (ext === "pdf") return "mdi-file-pdf-box";
      if (["doc", "docx"].includes(ext)) return "mdi-file-word-outline";
      return "mdi-file-document-outline";
    },

    // ⭐ เปิด dialog ยืนยันก่อนลบจริง (ป้องกันกดลบพลาด เพราะลบแล้วกู้คืนไม่ได้)
    confirmRemoveAttachment(filename) {
      this.fileToDelete = filename;
      this.dialogConfirmDelete = true;
    },

    // ⭐ ลบไฟล์แนบ 1 ไฟล์จริง — เรียก backend ที่จะลบทั้ง local file,
    // DB column file_attachments, และ sync ลบที่ Production/Backup Storage
    // ให้ครบในคำเดียว (ดู incorporate_docket.controller.js removeAttachment)
    async removeExistingAttachment(filename) {
      this.removingFile = filename;
      this.dialogConfirmDelete = false;
      try {
        await removeIncorporateDocketAttachment(this.docketId, filename);
        this.existingAttachments = this.existingAttachments.filter(
          (f) => f !== filename,
        );
        this.setupAlertDialog(
          true,
          "Success",
          "ลบไฟล์เรียบร้อยแล้ว",
          "text-h5 green--text text-center",
        );
      } catch (error) {
        console.error("Error removing attachment:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "ไม่สามารถลบไฟล์ได้ กรุณาลองใหม่",
          "text-h5 red--text text-center",
        );
      } finally {
        this.removingFile = null;
        this.fileToDelete = null;
      }
    },

    // ⭐ Single-select (radio behavior) — เลือกได้ทีละ 1 ค่า
    selectDocCategory(cat) {
      this.form.doc_category = this.form.doc_category === cat ? "" : cat;
    },

    // ⭐ Single-select (radio behavior) สำหรับ Change Tracker
    selectTracker(key) {
      const isSameSelected = this.form.change_tracker[0] === key;

      this.form.change_tracker.forEach((oldKey) => {
        this.$delete(this.selectedAgencies, oldKey);
      });

      if (isSameSelected) {
        this.form.change_tracker = [];
      } else {
        this.form.change_tracker = [key];
        this.$set(this.selectedAgencies, key, []);
      }
    },

    isAgencySelected(trackerKey, agency) {
      return (this.selectedAgencies[trackerKey] || []).includes(agency);
    },

    toggleAgency(trackerKey, agency) {
      if (!this.selectedAgencies[trackerKey]) {
        this.$set(this.selectedAgencies, trackerKey, []);
      }
      const list = this.selectedAgencies[trackerKey];
      const idx = list.indexOf(agency);
      if (idx >= 0) {
        list.splice(idx, 1);
      } else {
        list.push(agency);
      }
    },

    addShareholderRow() {
      this.trackerDetail.shareholders.push({
        full_name: "",
        nationality: "",
        ordinary_shares: 0,
        preferred_shares: 0,
      });
    },
    addDirectorRow() {
      this.trackerDetail.directors.push({
        full_name: "",
        nationality: "",
        authorized: "No",
      });
    },

    getDocketStatusColor(status) {
      const map = {
        "In Progress": "blue lighten-4",
        Complete: "green lighten-4",
        "Request Payment": "pink lighten-4",
        Cancelled: "red lighten-4",
      };
      return map[status] || "grey lighten-3";
    },
    getDocketStatusTextColor(status) {
      const map = {
        "In Progress": "blue darken-2",
        Complete: "green darken-2",
        "Request Payment": "pink darken-2",
        Cancelled: "red darken-2",
      };
      return map[status] || "grey darken-1";
    },

    validateForm() {
      if (!this.form.inquiry_topic || !this.form.inquiry_topic.trim()) {
        this.setupAlertDialog(
          true,
          "Check information",
          "กรุณากรอก Inquiry Topic",
          "text-h5 red--text text-center",
        );
        return false;
      }
      if (!this.form.date_received) {
        this.setupAlertDialog(
          true,
          "Check information",
          "กรุณากรอก Date Received",
          "text-h5 red--text text-center",
        );
        return false;
      }
      // ⭐ เพิ่มใหม่ — บังคับกรอก Next Due Date เพราะหน้า Incorporate
      // Overview ใช้ field นี้เป็นตัวหลักในการคำนวณ Overdue / Due Soon
      // และรายการ "Docket ที่ใกล้ครบกำหนด" ทั้งหมด (ดู upcomingDockets,
      // isOverdue ใน IncorporateOverview.vue) ถ้าเว้นว่างไว้ docket นี้
      // จะไม่ถูกนับรวมในหน้า Overview เลยโดยไม่มีการแจ้งเตือนใด ๆ
      if (!this.form.next_due_date) {
        this.setupAlertDialog(
          true,
          "Check information",
          "กรุณากรอก Next Due Date | วันครบกำหนดถัดไป (ใช้คำนวณสถานะ Overdue/Due Soon ในหน้า Overview)",
          "text-h5 red--text text-center",
        );
        return false;
      }
      for (const key of this.form.change_tracker) {
        const agencies = this.selectedAgencies[key] || [];
        if (agencies.length === 0) {
          const label =
            this.trackerOptions.find((t) => t.key === key)?.label || key;
          this.setupAlertDialog(
            true,
            "Check information",
            `กรุณาเลือกหน่วยงานที่ต้องดำเนินการสำหรับ "${label}" อย่างน้อย 1 หน่วยงาน`,
            "text-h5 red--text text-center",
          );
          return false;
        }
      }
      return true;
    },

    buildFormData() {
      const fd = new FormData();
      const company_id = localStorage.getItem(server.COMPANYID);
      const user_id = localStorage.getItem(server.USER_ID);

      if (this.form.matter_id) fd.append("matter_id", this.form.matter_id);
      fd.append("client_name", this.form.client_name);
      fd.append("inquiry_topic", this.form.inquiry_topic);
      fd.append("doc_category", this.form.doc_category || "");
      fd.append("change_tracker", this.changeTrackerLabel());

      // ⭐ แก้ไข (รอบ 2 — หลังตรวจ repository จริงแล้ว) — date_received และ
      // next_due_date ผ่านการบังคับกรอกจาก validateForm() มาแล้วเสมอ จึงส่ง
      // ค่าตรง ๆ ได้เลย ส่วน date_submitted (ไม่บังคับกรอก) ส่งกลับไปแบบเดิม
      // เสมอ (value || "") แทนที่จะ "ไม่ append field เลยเมื่อว่าง" ตามที่เคย
      // แก้ไว้รอบแรก เพราะพบว่า incorporate_docket.repository.js ฝั่ง
      // exports.update() ใช้ Sequelize Model.update() ซึ่งจะ "ข้าม field ที่
      // เป็น undefined ไปเฉย ๆ ไม่แตะค่าเดิมใน DB เลย" (ยืนยันจาก pattern
      // invoice_id ในไฟล์เดียวกัน: `data.invoice_id !== undefined ? ... :
      // undefined` ที่ตั้งใจอาศัยพฤติกรรมนี้) — ถ้าไม่ append field ตอนผู้ใช้
      // ลบ date_submitted ที่เคยกรอกไว้จนว่างเพื่อ "ล้างค่า" (เคสสำคัญของหน้า
      // Edit นี้โดยเฉพาะ) DB จะไม่ถูกล้างให้เลย จึงย้ายหน้าที่ sanitize
      // "" → null ไปทำที่ repository แทน (ดู sanitizeDateOnly() ใน
      // incorporate_docket.repository.js) แล้วให้ frontend ส่งค่าตรง ๆ
      // เสมอแบบง่าย ๆ พอ — วิธีนี้ทำให้ "ล้างค่า date_submitted" ทำงานถูกต้อง
      fd.append("date_received", this.form.date_received);
      fd.append("next_due_date", this.form.next_due_date);
      fd.append("date_submitted", this.form.date_submitted || "");

      fd.append("status", this.form.status);
      fd.append("remark", this.form.remark || "");
      fd.append("company_id", company_id);
      fd.append("user_update", user_id);
      fd.append("quotation_id", Number(this.formQutations.salesOrderId));

      // ⭐ เฉพาะไฟล์ใหม่ (form.attachments) เท่านั้นที่ส่งเป็น multipart
      // ไฟล์เดิม (existingAttachments) ไม่ต้องส่งซ้ำ — backend ฝั่ง update
      // จะ append ไฟล์ใหม่ต่อจาก file_attachments เดิมที่มีอยู่ในแล้วอัตโนมัติ
      // (ดู incorporate_docket.controller.js exports.update)
      if (this.form.attachments && this.form.attachments.length > 0) {
        this.form.attachments.forEach((file) => fd.append("file", file));
      }

      return fd;
    },

    changeTrackerLabel() {
      return this.form.change_tracker
        .map((key) => {
          const opt = this.trackerOptions.find((t) => t.key === key);
          return opt ? opt.label.replace(/^[^\s]+\s/, "") : key;
        })
        .join(", ");
    },

    buildChangesPayload() {
      return this.form.change_tracker.map((key) => {
        let change_detail = {};

        switch (key) {
          case "shareholders":
            change_detail = {
              items: this.trackerDetail.shareholders
                .filter((s) => s.full_name && s.full_name.trim() !== "")
                .map((s) => ({
                  full_name: s.full_name,
                  nationality: s.nationality,
                  ordinary_shares: Number(s.ordinary_shares) || 0,
                  preferred_shares: Number(s.preferred_shares) || 0,
                })),
            };
            break;
          case "directors":
            change_detail = {
              items: this.trackerDetail.directors
                .filter((d) => d.full_name && d.full_name.trim() !== "")
                .map((d) => ({
                  full_name: d.full_name,
                  nationality: d.nationality,
                  authorized: d.authorized,
                })),
            };
            break;
          case "reg-capital":
            change_detail = {
              amount: Number(this.trackerDetail.reg_capital) || 0,
            };
            break;
          case "paidup-capital":
            change_detail = {
              amount: Number(this.trackerDetail.paidup_capital) || 0,
            };
            break;
          case "company-name":
            change_detail = { name: this.trackerDetail.company_name || "" };
            break;
          case "reg-address":
            change_detail = { address: this.trackerDetail.reg_address || "" };
            break;
          case "auditor":
            change_detail = {
              audit_firm: this.trackerDetail.audit_firm || "",
              auditor_name: this.trackerDetail.auditor_name || "",
              auditor_license_no: this.trackerDetail.auditor_license_no || "",
            };
            break;
          case "accountant":
            change_detail = {
              accountant_firm: this.trackerDetail.accountant_firm || "",
              accountant_name: this.trackerDetail.accountant_name || "",
            };
            break;
          case "others":
            change_detail = { detail: this.trackerDetail.others_detail || "" };
            break;
        }

        return {
          change_type: key,
          change_detail,
          agencies: this.selectedAgencies[key] || [],
        };
      });
    },

    // ⭐ บันทึกแก้ไขจริง — ทำ 3 ขั้นตอนต่อกัน:
    // 1) แก้ไข docket หลัก (multipart/form-data — อัปโหลดไฟล์ใหม่ถ้ามี) — เดิม
    // 2) ⭐ เพิ่ม — สร้าง timeline record ใหม่ + regenerate PDF client
    //    progress ผูกกับ docketId เดิม (มีอยู่แล้วตั้งแต่เปิดหน้า Edit
    //    ต่างจาก add.vue ที่ต้องรอสร้าง docket ก่อนถึงจะมี id)
    // 3) replace-all Change Tracker ทั้งชุด — เดิม

    async updateDocket(mode) {
      if (!this.validateForm()) return; // เดิม ไม่แตะ

      // ⭐ เพิ่ม — บังคับให้กรอก Internal Timeline ก่อน save เสมอ
      // เหมือนกับ incorporate-docket-add.vue
      if (this.timelineStatus == 0 || this.status_note.trim() == "") {
        this.dialogDelete = true;
        return;
      }

      this.loadingSave = true; // เดิม ไม่แตะ
      this.$showLoader(); // เดิม ไม่แตะ

      try {
        const fd = this.buildFormData(); // เดิม ไม่แตะ
        await updateIncorporateDocket(this.docketId, fd); // เดิม ไม่แตะ
        try {
          if (this.quotationscheckstatus == "Submitted") {

            var dataupdateStatus = {
              status: "InProgress"
            }
            await api.updateQuotationsCore(Number(this.formQutations.salesOrderId), dataupdateStatus)
          }
        } catch (quotationsError) {
          console.log(quotationsError)
        }
        // ⭐ เพิ่ม — บันทึก timeline history record ใหม่ ผูกกับ docketId
        // เดิมที่มีอยู่แล้ว (ไม่ต้องมี main_id ชั่วคราวแบบ add.vue)
        const dataadd_timeline = {
          timeline_status_id: Number(this.timelineStatus),
          case_id: Number(this.docketId),
          module_name: "Corporate",
          case_woker: Number(localStorage.getItem(server.USER_ID)),
          status_time: moment().format("YYYY-MM-DD HH:mm:ss"),
          status_note: this.status_note.trim(),
          visibility: this.visible_check,
        };
        await api.creategetClientTimelineHistory(dataadd_timeline);

        // ⭐ เพิ่ม — regenerate PDF client progress เหมือน add.vue
        // ไม่ throw ต่อถ้า fail (ตัดขาดจาก flow หลัก ไม่ให้กระทบการบันทึก
        // docket / change tracker ที่เหลือ)
        try {
          const caseDetailResult = await api.getgetTimelineDataDetail({
            params: {
              module_name: this.module_name,
              case_id: Number(this.docketId),
            },
          });
          const caseInfo = { ...(caseDetailResult.data?.[0] || {}) };

          const visibilityResult =
            await api.getgetClientTimelineHistoryShowVisibility({
              params: {
                module_name: this.module_name,
                case_id: Number(this.docketId),
              },
            });
          const timelineData = visibilityResult.data || [];

          caseInfo.status = timelineData[0]?.status || "";
          caseInfo.lastUpdated = timelineData[0]?.status_time || "";

          const latestUpdate = {
            message: timelineData[0]?.status_note || "",
            date: timelineData[0]?.status_time || "",
          };

          await PDFCaseProgress.createToPDF(
            {
              caseInfo,
              latestUpdate,
              timelineData,
              caseId: Number(this.docketId),
              moduleName: this.module_name,
            },
            { open: false }
          );
        } catch (pdfError) {
          console.error("PDF generation/upload failed:", pdfError);
          // ไม่ throw ต่อ — ตัดขาดจาก flow หลัก
        }

        // ↓↓↓ เดิม ไม่แตะ ↓↓↓
        const user_id = localStorage.getItem(server.USER_ID);
        const changes = this.buildChangesPayload();
        await replaceIncorporateDocketChangeAll(this.docketId, {
          changes,
          user_id,
        });

        this.$hideLoader();
        this.loadingSave = false;
        if (mode == "Save") {
          this.setupAlertDialogPush(
            true,
            "Success",
            "บันทึกข้อมูลสำเร็จ",
            "text-h5 green--text text-center",
            `/incorporate-docket-view/${this.docketId}`,
          );
        } else {
          var mainid = this.docketId;
          var matterid = this.form.matter_id;


          var customer_list = this.matterdatalist.find(
            item => item.id === matterid
          )
          var customer_name_master_show = this.client_name_show
          var clientid = customer_list.client_id;

          var dataPush = {
            customer_name_master: customer_name_master_show,
            module_id: 0,
            quotation_id: Number(this.formQutations.salesOrderId),
          }

          dataPush.module_id = Number(mainid);

          this.$router.push({
            path: `/new-free-invoice/0/invoice/D/${clientid}/Incorporation`,
            query: dataPush,
          });
        }
      } catch (error) {
        this.$hideLoader();
        this.loadingSave = false;
        console.error("Error updating docket:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "เกิดข้อผิดพลาดในการบันทึก",
          "text-h5 red--text text-center",
        );
      }
    },

    // ⭐ FIX: คอมโพเนนต์ SuccessDialog.vue ผูก v-model กับ
    // $store.state.global_dialog โดยตรง (ไม่ได้ใช้ "status" prop ที่ส่งเข้าไป
    // เลยแม้แต่จุดเดียว) ดังนั้นถ้าตั้งแค่ this.dialogAdd = status เฉย ๆ
    // popup จะไม่เด้งขึ้นมาเลย — ต้อง sync เข้า store ทุกครั้งที่เรียกใช้
    setupAlertDialog(status, title, message, text_color) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
      this.$store.state.global_dialog = status;
    },
    setupAlertDialogPush(status, title, message, text_color, link) {
      this.title = title;
      this.message = message;
      this.dialogAddPush = status;
      this.text_color = text_color;
      this.link = link;
      this.$store.state.global_push_dialog = status;
    },
  },
};
</script>

<style scoped>
.business-rule-alert {
  background-color: #fffbeb !important;
  border: 1.5px solid #fbbf24 !important;
  color: #92400e !important;
}

.business-rule-alert>>>.v-alert__content {
  color: #92400e !important;
}

.business-rule-alert strong {
  color: #78350f !important;
}

.bg-colorth {
  background-color: #8d8d8d !important;
}

.tabletextwhite {
  color: white !important;
}

.form-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  border-left: 4px solid #0a7ea5;
  padding-left: 10px;
}

.upload-zone {
  border: 1.5px dashed #cbd5e1 !important;
  background: #f9fafb;
}

.attachment-row {
  border-bottom: 1px solid #f1f5f9;
}

.attachment-row:last-child {
  border-bottom: none;
}

/* Document Category items */
.doccat-item {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.15s;
  background: #fff;
}

.doccat-item:hover {
  border-color: #93c5fd;
  background: #eff6ff;
}

.doccat-selected {
  border-color: #2563eb !important;
  background: #eff6ff !important;
}

.doccat-label {
  font-size: 13px;
  color: #374151;
}

/* Change Tracker items */
.tracker-item {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.15s;
  background: #fff;
}

.tracker-item:hover {
  border-color: #93c5fd;
  background: #eff6ff;
}

.tracker-selected {
  border-color: #2563eb !important;
  background: #eff6ff !important;
}

/* Radio indicator ใช้ร่วมกันทั้ง doc category และ change tracker */
.tracker-radio {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tracker-radio-checked {
  border-color: #2563eb;
}

.tracker-radio-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #2563eb;
}

/* Repeatable table (shareholders / directors) */
.repeatable-table {
  border-collapse: collapse;
}

.repeatable-table th {
  background: #eef2ff;
  padding: 8px 10px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  border: 1px solid #e5e7eb;
}

.repeatable-table td {
  padding: 7px 10px;
  border: 1px solid #e5e7eb;
}

/* Agency selection block */
.agency-block {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1.5px dashed #f59e0b;
  border-radius: 8px;
  background: #fffbeb;
}

.agency-title {
  font-size: 12px;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.agency-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11.5px;
  color: #374151;
  border: 1.5px solid #fde68a;
  border-radius: 6px;
  padding: 7px 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
}

.agency-item:hover {
  border-color: #f59e0b;
}

.agency-item-selected {
  border-color: #f59e0b !important;
  background: #fef3c7 !important;
}

.info-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #c47f00;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  cursor: help;
}
</style>